import { useState } from "react";
import { Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { EXTRA_CATS, fmtDay, dateGroupHint } from "./budgetUtils";

export const ExtraEditForm = ({ tripId, item, splitExpense, members, perDay, onSaved, onCancel }) => {
  const hasDates = Boolean(perDay?.some((d) => d.date));
  const [form, setForm] = useState(() => ({
    label: item.label,
    amount: String(item.amount_eur),
    day: item.day_number != null ? String(item.day_number) : "",
    date: item.date || (item.day_number != null ? (perDay?.find((d) => d.day_number === item.day_number)?.date || "") : ""),
    category: item.category || "overig",
    kwh: item.kwh != null ? String(item.kwh) : "",
    paidBy: splitExpense?.paid_by || "",
    parts: splitExpense ? [...splitExpense.participants] : [...members],
  }));
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/trips/${tripId}/extra-costs/${item.id}`);
      toast.success("Onkost verwijderd");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(false);
      setConfirmDel(false);
    }
  };

  const save = async () => {
    if (!form.label.trim() || !form.amount) return;
    if (form.paidBy && (form.parts || []).length === 0) {
      toast.error("Kies minstens één reisgenoot om over te verdelen");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/trips/${tripId}/extra-costs/${item.id}`, {
        label: form.label.trim(),
        amount_eur: parseFloat(form.amount),
        day_number: hasDates ? null : (form.day === "" ? null : parseInt(form.day)),
        date: hasDates ? (form.date || null) : null,
        category: form.category || null,
        kwh: form.kwh === "" ? null : parseFloat(form.kwh),
        split_paid_by: form.paidBy || null,
        split_participants: form.paidBy ? form.parts : null,
      });
      toast.success("Onkost aangepast");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`extra-cost-edit-form-${item.id}`} className="bg-secondary/50 rounded-xl p-3 my-1.5">
      <div className="grid grid-cols-2 gap-2">
        <input data-testid="extra-edit-label" value={form.label} maxLength={80}
          onChange={(ev) => setForm((f) => ({ ...f, label: ev.target.value }))}
          className="col-span-2 bg-card rounded-xl px-3.5 py-2 text-sm outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200" />
        <input data-testid="extra-edit-amount" type="number" min="0" step="0.01" value={form.amount}
          onChange={(ev) => setForm((f) => ({ ...f, amount: ev.target.value }))} placeholder="€"
          className="bg-card rounded-xl px-3.5 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200" />
        <select data-testid="extra-edit-category" value={form.category}
          onChange={(ev) => setForm((f) => ({ ...f, category: ev.target.value }))}
          className="bg-card rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200">
          {EXTRA_CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {hasDates ? (
          <input data-testid="extra-edit-date" type="date" value={form.date}
            onChange={(ev) => setForm((f) => ({ ...f, date: ev.target.value }))}
            title="Datum van de uitgave — mag ook vóór of na de reis liggen"
            className="bg-card rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200" />
        ) : (
        <select data-testid="extra-edit-day" value={form.day}
          onChange={(ev) => setForm((f) => ({ ...f, day: ev.target.value }))}
          className="bg-card rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200">
          <option value="">Hele trip</option>
          {perDay?.map((d) => <option key={d.day_number} value={d.day_number}>{`Dag ${d.day_number}${d.date ? ` · ${fmtDay(d.date)}` : ""}`}</option>)}
        </select>
        )}
        {form.category === "laden" && (
          <input data-testid="extra-edit-kwh" type="number" min="0" step="0.1" value={form.kwh}
            onChange={(ev) => setForm((f) => ({ ...f, kwh: ev.target.value }))} placeholder="kWh (optioneel)"
            title="Aantal geladen kWh (optioneel)"
            className="bg-card rounded-xl px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200" />
        )}
      </div>
      {hasDates && (
        <p className="text-[10px] text-muted-foreground mt-1" data-testid="extra-edit-date-hint">{dateGroupHint(form.date, perDay)}</p>
      )}
      {members.length > 0 && (
        <div className="mt-2.5 bg-card/70 rounded-lg p-2.5">
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="font-bold">Verdelen met reisgenoten:</span>
            <select data-testid="extra-edit-paidby" value={form.paidBy}
              onChange={(ev) => setForm((f) => ({ ...f, paidBy: ev.target.value, parts: (f.parts || []).length ? f.parts : [...members] }))}
              className="bg-secondary rounded-lg px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200">
              <option value="">niet verdelen</option>
              {members.map((m) => <option key={m} value={m}>betaald door {m}</option>)}
            </select>
            {item.split_expense_id && !form.paidBy && <span className="italic text-[10px]" data-testid="extra-edit-unsplit-hint">— de verdeling wordt verwijderd bij opslaan</span>}
          </div>
          {form.paidBy && members.length > 1 && (
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5 text-xs text-muted-foreground" data-testid="extra-edit-parts">
              <span>verdeeld over:</span>
              {members.map((m) => {
                const on = (form.parts || []).includes(m);
                return (
                  <button key={m} type="button" data-testid={`extra-edit-part-${m}`}
                    onClick={() => setForm((f) => ({ ...f, parts: on ? f.parts.filter((x) => x !== m) : [...(f.parts || []), m] }))}
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-[background-color,color,border-color] duration-150 ${on ? "bg-[#3A6EA5] text-white border-[#3A6EA5]" : "bg-card border-border text-muted-foreground hover:border-[#3A6EA5]/50"}`}>
                    {m}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 mt-2.5">
        <button data-testid="extra-edit-save" onClick={save} disabled={busy || !form.label.trim() || !form.amount}
          className="rounded-full bg-primary hover:bg-[#1B4332] disabled:opacity-40 text-primary-foreground text-xs font-bold py-2 px-4 flex items-center gap-1 active:scale-95 transition-[transform,background-color] duration-200">
          <Check className="w-3.5 h-3.5" /> {busy ? "..." : "Opslaan"}
        </button>
        <button data-testid="extra-edit-cancel" onClick={onCancel}
          className="text-xs text-muted-foreground hover:text-foreground transition-[color] duration-200">Annuleer</button>
        <div className="ml-auto">
          {confirmDel ? (
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground">Zeker?</span>
              <button data-testid="extra-edit-delete-confirm" onClick={remove} disabled={busy}
                className="rounded-full bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white text-xs font-bold py-2 px-3.5 flex items-center gap-1 active:scale-95 transition-[transform,background-color] duration-200">
                <Trash2 className="w-3.5 h-3.5" /> {busy ? "..." : "Ja, verwijder"}
              </button>
              <button data-testid="extra-edit-delete-cancel" onClick={() => setConfirmDel(false)} disabled={busy}
                className="text-xs text-muted-foreground hover:text-foreground transition-[color] duration-200">Nee</button>
            </div>
          ) : (
            <button data-testid="extra-edit-delete" onClick={() => setConfirmDel(true)} disabled={busy}
              title="Verwijder deze onkost"
              className="rounded-full border border-red-200 text-red-600 hover:bg-red-50 text-xs font-semibold py-2 px-3.5 flex items-center gap-1 active:scale-95 transition-[transform,background-color] duration-200">
              <Trash2 className="w-3.5 h-3.5" /> Verwijder
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
